import type { UIBreadcrumbsItem } from '@design-system/components/UI/UIBreadcrumbs.props'

/**
 * Generates a schema.org breadcrumb structure for SEO optimization.
 *
 * @param {UIBreadcrumbsItem[] | undefined} data - An array of breadcrumb items containing labels and optional URLs.
 * @returns {void}
 *
 * @description
 * This composable creates a structured breadcrumb schema using schema.org vocabulary.
 * It helps improve search engine understanding of page hierarchy and navigation.
 *
 * @example
 * // Example usage with breadcrumb items
 * useCustomBreadcrumbsSchema([
 *   { label: 'Home', url: '/' },
 *   { label: 'Products', url: '/products' },
 *   { label: 'Current Page' }
 * ])
 *
 * @remarks
 * - Returns undefined if no data is provided
 * - Uses canonical and absolute site path resolution
 * - Supports breadcrumb items with or without URLs
 */
export const useCustomBreadcrumbsSchema = (
  data: UIBreadcrumbsItem[] | undefined
) => {
  if (!data || !data.length) return
  const siteResolver = createSitePathResolver({
    canonical: true,
    absolute: true,
  })

  useSchemaOrg([
    defineBreadcrumb({
      itemListElement: data.map(({ label, url }) => ({
        name: label,
        ...(url ? { item: siteResolver(url).value } : {}),
      })),
    }),
  ])
}
